import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { FiscalYearContext } from '../context/FiscalYearContext';
import { formatFiscalYear } from '../utils/fiscalYear';

import {
    getDashboardMetrics,
    getEnrollmentsData,
    getTopCourses,
    DashboardMetrics,
    EnrollmentData,
    TopCourse
} from '../api/dashboard';

import Sidebar from '../components/Sidebar';
import '../styles/Dashboard.css';

const IconUsers = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3Zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3Zm0 2c-2.33 0-7 1.17-7 3.5V19h10v-2.5C11 14.17 6.33 13 4 13Zm12 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.98 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5Z"/>
    </svg>
);

const IconUserCheck = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4S8 5.79 8 8s1.79 4 4 4Zm-7 8v-1c0-2.76 4.48-4 7-4s7 1.24 7 4v1H5Zm13.59-9.41 1.41 1.41L17 14l-2.59-2.59 1.41-1.41L17 11.17l2.59-2.58Z"/>
    </svg>
);

const IconCurrency = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor" d="M12 2C6.48 2 2 6.03 2 11c0 4.3 3.44 7.86 8 8.82V22h2v-2.11c3.76-.44 6.73-3.08 7.63-6.49l-1.94-.51C16.93 15.9 14.63 18 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c2.63 0 4.93 2.1 5.69 4.61l1.94-.51C20.73 5.97 17.76 3.33 14 2.89V1h-2v1.11C11.33 2.04 11.67 2 12 2Z"/>
    </svg>
);

const IconReceipt = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor" d="M19 2H5a2 2 0 0 0-2 2v18l3-1.5L9 22l3-1.5L15 22l3-1.5L22 22V4a2 2 0 0 0-2-2ZM7 6h10v2H7V6Zm0 4h10v2H7v-2Zm0 4h7v2H7v-2Z"/>
    </svg>
);

const IconTrendUp = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor" d="M16 6h5v5h-2V9.41l-6.59 6.59L8 11.59l-5.29 5.3-1.42-1.42L8 8.75l4.41 4.41L18.59 7H16V6Z"/>
    </svg>
);

const IconClock = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor" d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2Zm1 11h5v-2h-4V7h-2v6Z"/>
    </svg>
);

const IconArrowUp = () => (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
        <path fill="currentColor" d="M10 3l5 6h-3v8H8V9H5l5-6z"/>
    </svg>
);

export default function Dashboard() {
    const { user } = useContext(AuthContext);
    const { fiscalYear } = useContext(FiscalYearContext);

    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [enrollmentsData, setEnrollmentsData] = useState<EnrollmentData[]>([]);
    const [topCourses, setTopCourses] = useState<TopCourse[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user || user.role === 'viewer') {
            setLoading(false);
            return;
        }
        let active = true;
        (async () => {
            try {
                const [metricsData, enrollments, courses] = await Promise.all([
                    getDashboardMetrics(fiscalYear),
                    getEnrollmentsData(),
                    getTopCourses(fiscalYear),
                ]);
                if (!active) return;
                setMetrics(metricsData);
                setEnrollmentsData(enrollments);
                setTopCourses(courses);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [user, fiscalYear]);

    if (!user) return <Navigate to="/" replace />;

    if (user.role === 'viewer')
        return (
            <div>
                <Sidebar />
                <main className="dashboard-content">
                    <div className="dashboard-header">
                        <h1>Welcome, viewer!</h1>
                        <p className="dashboard-subtitle">Limited access dashboard</p>
                    </div>
                </main>
            </div>
        );

    const totalIncome = metrics?.totalIncome || 0;
    const totalExpense = metrics?.totalExpenses || 0;
    const totalProfit = metrics?.profit || 0;
    const totalStudents = metrics?.totalStudents || 0;
    const activeStudents = metrics?.activeStudents || 0;
    const pendingDues = metrics?.pendingDues || 0;

    const profitMargin = totalIncome > 0 ? ((totalProfit / totalIncome) * 100).toFixed(1) : '0.0';
    const activePct = totalStudents > 0 ? ((activeStudents / totalStudents) * 100).toFixed(1) : '0.0';

    return (
        <div>
            <Sidebar />
            <main className="dashboard-content">
                <div className="dashboard-header">
                    <h1>{process.env.REACT_APP_COMPANY_NAME || 'Care Kathmandu'} Dashboard</h1>
                    <p className="dashboard-subtitle">Student overview · Financials for {formatFiscalYear(fiscalYear)}</p>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading dashboard data...</p>
                    </div>
                ) : (
                    <>
                        {/* Summary cards */}
                        <div className="summary-grid">
                            <div className="summary-card">
                                <div className="card-icon -primary"><IconUsers /></div>
                                <div className="card-content">
                                    <h3>Total Students</h3>
                                    <p className="card-value">{totalStudents.toLocaleString()}</p>
                                    <div className="trend"><span className="trend-up"><IconArrowUp /> +12%</span> from last month</div>
                                </div>
                            </div>

                            <div className="summary-card">
                                <div className="card-icon -primary"><IconUserCheck /></div>
                                <div className="card-content">
                                    <h3>Total Active Students</h3>
                                    <p className="card-value">{activeStudents.toLocaleString()}</p>
                                    <div className="trend">
                                        <span className="muted">{activePct}% of total</span>
                                        <span className="dot-sep">•</span>
                                        <span className="trend-up"><IconArrowUp /> +8%</span> from last month
                                    </div>
                                </div>
                            </div>

                            <div className="summary-card">
                                <div className="card-icon -success"><IconCurrency /></div>
                                <div className="card-content">
                                    <h3>Total Income Collected</h3>
                                    <p className="card-value">₹{totalIncome.toLocaleString('en-US')}</p>
                                    <div className="trend"><span className="trend-up"><IconArrowUp /> +15%</span> from last month</div>
                                </div>
                            </div>

                            <div className="summary-card">
                                <div className="card-icon -warning"><IconReceipt /></div>
                                <div className="card-content">
                                    <h3>Total Expenses</h3>
                                    <p className="card-value">₹{totalExpense.toLocaleString('en-US')}</p>
                                    <div className="trend"><span className="muted">Operating costs</span></div>
                                </div>
                            </div>

                            <div className="summary-card">
                                <div className="card-icon -violet"><IconTrendUp /></div>
                                <div className="card-content">
                                    <h3>Profit</h3>
                                    <p className="card-value">₹{totalProfit.toLocaleString('en-US')}</p>
                                    <div className="trend">
                                        <span className="muted">{profitMargin}% margin</span>
                                        <span className="dot-sep">•</span>
                                        <span className="trend-up"><IconArrowUp /> +22%</span> from last month
                                    </div>
                                </div>
                            </div>

                            <div className="summary-card">
                                <div className="card-icon -slate"><IconClock /></div>
                                <div className="card-content">
                                    <h3>Pending Dues</h3>
                                    <p className="card-value">₹{pendingDues.toLocaleString('en-US')}</p>
                                    <div className="trend"><span className="muted">Overdue: {Math.max(0, Math.round(pendingDues / 7200))}</span></div>
                                </div>
                            </div>
                        </div>

                        {/* Body sections */}
                        <div className="grid-two">
                            {/* Enrollments */}
                            <section className="panel">
                                <div className="panel-header">
                                    <h2>📈 Enrollments (Past 12 Months)</h2>
                                </div>

                                {enrollmentsData.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-ring" />
                                        <p>Chart will be implemented with real data</p>
                                    </div>
                                ) : (
                                    <div className="table-container">
                                        <table className="modern-table">
                                            <thead>
                                            <tr>
                                                <th>Month</th>
                                                <th>Year</th>
                                                <th>Enrollments</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {enrollmentsData.map((enrollment, index) => (
                                                <tr key={index} className="table-row">
                                                    <td className="month-cell">
                                                        {new Date(0, enrollment.month - 1).toLocaleDateString('en-US', { month: 'long' })}
                                                    </td>
                                                    <td className="year-cell">{enrollment.year}</td>
                                                    <td className="count-cell">{enrollment.count}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            {/* Top Courses */}
                            <section className="panel">
                                <div className="panel-header">
                                    <h2>🏆 Top 5 Performing Courses</h2>
                                </div>

                                {topCourses.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No course performance data to display.</p>
                                    </div>
                                ) : (
                                    <ul className="course-list">
                                        {topCourses.slice(0, 5).map((c, idx) => {
                                            // simple derived percent if totalIncome > 0
                                            const percent = totalIncome > 0 ? Math.round((c.totalIncome / totalIncome) * 1000) / 10 : 0;
                                            return (
                                                <li key={c.courseId} className="course-item">
                                                    <div className="course-left">
                                                        <div className="rank-bullet">{idx + 1}</div>
                                                        <div>
                                                            <div className="course-title">{c.courseName}</div>
                                                            <div className="course-sub">Income: ₹{c.totalIncome.toLocaleString('en-US')}</div>
                                                        </div>
                                                    </div>
                                                    <div className="course-right">
                                                        <div className="amount">₹{c.totalIncome.toLocaleString('en-US')}</div>
                                                        <span className="pill pill-green">{percent}%</span>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </section>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
